import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import {
  getPendingAuthorization,
  decideAuthorization,
  ApiError,
  type PendingAuthorization,
} from '../api';

/**
 * The consent screen: the whole point of the OAuth door, seen from the person's side.
 *
 * The other way into `/mcp` is a token minted in Settings and pasted into a JSON file. This is the
 * same credential, with the same scopes, minted by somebody pressing a button instead — so nothing
 * downstream changes, and what a person is asked is the one question that actually matters: *this
 * program wants to do these things to your account; may it?*
 *
 * ## Why it is not the Settings picker verbatim
 *
 * The task body said "requested scopes rendered with the existing picker". Nearly: the tick boxes
 * are the same control with the same classes and `delete` is off by default exactly as it is there
 * — but the list is **what this client asked for**, not `SCOPE_LIST`. Offering the whole
 * vocabulary would invite somebody to grant more than was requested, which the server refuses
 * anyway (`grantAuthorization` intersects with the ask), so the boxes would be a promise the API
 * does not keep. The name, expiry and project fields are absent for the same reason in reverse:
 * they are the minter's decisions, and here the minter is the client.
 *
 * ## No new CSS
 *
 * Every class here already has a rule — `workspace`, `main-pane`, `card`, `muted`, `scope-picker`,
 * `check`, `dangerous`, `detail-foot`, `primary`, `banner`. That is deliberate rather than lucky:
 * this screen is a form in a card inside the ordinary pane, which is what `NewTokenForm` in
 * Settings already is, and `classesAreDefined.spec.ts` is the guard that would have caught
 * inventing one.
 */
export function Consent({ requestId }: { requestId: string }) {
  const pending = useQuery({
    queryKey: ['oauth-consent', requestId],
    queryFn: () => getPendingAuthorization(requestId),
    retry: false,
  });

  return (
    <div className="workspace">
      <div className="main-pane">
        <QueryState query={pending} what="this request" />
        {pending.data && <ConsentForm pending={pending.data} />}
      </div>
    </div>
  );
}

function ConsentForm({ pending }: { pending: PendingAuthorization }) {
  const [scopes, setScopes] = useState<string[]>(pending.defaultScopes);
  const toggle = (scope: string) =>
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );

  /*
    Both answers leave this application, so neither renders a result.

    `window.location.assign` rather than React state: `redirectTo` is the client's own callback on
    another origin, and a denial goes there too — a client that is told nothing cannot tell its own
    user why the connection did not happen, and would sit waiting.
  */
  const decide = useMutation({
    mutationFn: (decision: 'allow' | 'deny') =>
      decideAuthorization({ request: pending.requestId, decision, scopes }),
    onSuccess: (result) => window.location.assign(result.redirectTo),
  });

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        if (scopes.length) decide.mutate('allow');
      }}
    >
      <h2>Connect {pending.clientName}?</h2>
      <p className="muted">
        It is asking to use your account through this app’s API. Untick anything it does not need —
        it gets exactly what you leave ticked, and nothing it did not ask for. You can revoke it at
        any time in Settings › API tokens.
      </p>

      <h3>What it will be able to do</h3>
      <ul className="scope-picker">
        {pending.requestedScopes.map((scope) => (
          <li key={scope}>
            <label className={`check ${scope === 'delete' ? 'dangerous' : ''}`}>
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={() => toggle(scope)}
              />
              {scope}
            </label>
          </li>
        ))}
      </ul>
      {/*
        The `delete` warning as its own line, rather than trailing the tick box the way Settings
        writes it.

        Two reasons, and the measured one first: `.check` is `white-space: nowrap`, so
        "delete — permanent deletion, off by default" inside the label pushed the page 5px past
        375 and the whole screen scrolled sideways. `oauthConsent.e2e.spec.ts` is what caught it.
        The other is that the sentence stops being true the moment somebody ticks the box — "off
        by default" is a fact about the form's initial state, and this is a warning about what
        they are agreeing to, which they need either way.
      */}
      {pending.requestedScopes.includes('delete') && (
        <p className="muted">
          <strong>delete</strong> is permanent deletion, with no undo. It is left unticked whatever
          the client asked for; tick it only if you know this program needs it.
        </p>
      )}

      {decide.error instanceof ApiError && <p className="banner">{decide.error.message}</p>}

      <div className="detail-foot">
        <button
          type="button"
          onClick={() => decide.mutate('deny')}
          disabled={decide.isPending || decide.isSuccess}
        >
          Deny
        </button>
        <button
          type="submit"
          className="primary"
          disabled={scopes.length === 0 || decide.isPending || decide.isSuccess}
        >
          Allow
        </button>
      </div>
    </form>
  );
}

/**
 * The request id this page is about, or null when the browser is not on the consent page.
 *
 * Read off `window.location` because this application has no router — the same arrangement
 * `invitationTokenFromUrl` uses for an invitation link, and for the same reason: a deep link
 * arrives as a path, and there is exactly one screen that needs to notice.
 */
export function consentRequestFromUrl(
  location: { pathname: string; search: string } = window.location,
): string | null {
  if (location.pathname !== '/oauth/consent') return null;
  return new URLSearchParams(location.search).get('request');
}
